import React, { useMemo } from "react";
import { Modal, Text } from "@jsq/ds/core";
import { type ExtensionFieldObjectType } from "~/graphql/supergraphTypes";
import { EntitySummary } from "./EntitySummary";
import { ExtensionFieldValuesList } from "./ExtensionFieldValuesList";
import * as styles from "./styles_vanilla.css";
import type { SectionWithFields, TabWithSections } from "../../../CustomFields/types";
import type {
  InvestmentEntityWithExtensionFieldsGridRow,
  EntityExtensionFieldValue,
} from "../../types";

interface EntityDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedEntity: InvestmentEntityWithExtensionFieldsGridRow | null;
  extensionFields: ExtensionFieldObjectType[];
  sectionsWithFields: SectionWithFields[];
  tabsWithSections: TabWithSections[];
  onDataRefresh?: () => void;
}

/**
 * Entity Detail Modal with inline editing for extension field values.
 * Features a two-column layout with entity summary and editable field values.
 */
export const EntityDetailModal: React.FC<EntityDetailModalProps> = ({
  isOpen,
  onClose,
  selectedEntity,
  extensionFields,
  sectionsWithFields,
  tabsWithSections,
  onDataRefresh,
}) => {
  const extensionFieldValues = useMemo((): EntityExtensionFieldValue[] => {
    if (!selectedEntity) return [];

    const allFieldValues: EntityExtensionFieldValue[] = extensionFields.map((field) => {
      const value = selectedEntity.extensionFieldValues?.[field.id] || null;

      return {
        field,
        value,
        isEditing: false,
        isDirty: false,
      };
    });

    return allFieldValues;
  }, [selectedEntity, extensionFields]);

  // Don't render if no entity is selected
  if (!selectedEntity) {
    return null;
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="xl"
      data-testid="entity-detail-modal"
      className={styles.modal}
    >
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.modalContent}>
          {/* Left Sidebar - Entity Summary */}
          <div className={styles.sidebar}>
            <EntitySummary entity={selectedEntity} />
          </div>

          {/* Right Content Area - Extension Field Values */}
          <div className={styles.mainContent}>
            {extensionFieldValues.length > 0 ? (
              <ExtensionFieldValuesList
                extensionFieldValues={extensionFieldValues}
                sectionsWithFields={sectionsWithFields}
                tabsWithSections={tabsWithSections}
                entityId={parseInt(selectedEntity.id, 10)}
                onDataRefresh={onDataRefresh}
                onModalClose={onClose}
              />
            ) : (
              <div className={styles.emptyContainer} data-testid="empty-state">
                <Text variant="regular-md" className={styles.emptyTitle}>
                  No Extension Fields
                </Text>
                <Text variant="regular-sm" className={styles.emptyMessage}>
                  This entity has no extension field values configured.
                </Text>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EntityDetailModal;
